import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";

interface TimeEntryRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (note: string) => Promise<void>;
  entryInfo: {
    providerName: string;
    facilityName: string;
    date: string;
  } | null;
  isRejecting: boolean;
}

export function TimeEntryRejectDialog({
  open,
  onOpenChange,
  onConfirm,
  entryInfo,
  isRejecting,
}: TimeEntryRejectDialogProps) {
  const [rejectionNote, setRejectionNote] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    if (!rejectionNote.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    await onConfirm(rejectionNote);
    setRejectionNote("");
    setError("");
  };

  const handleCancel = () => {
    setRejectionNote("");
    setError("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto bg-amber-100 p-3 rounded-full mb-4 w-12 h-12 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-amber-600" />
          </div>
          <DialogTitle className="text-center">Reject Time Entry</DialogTitle>
          <DialogDescription className="text-center">
            {entryInfo && (
              <>
                Rejecting time entry for{" "}
                <span className="font-medium text-slate-700">
                  {entryInfo.providerName}
                </span>{" "}
                at{" "}
                <span className="font-medium text-slate-700">
                  {entryInfo.facilityName}
                </span>{" "}
                on{" "}
                <span className="font-medium text-slate-700">
                  {entryInfo.date}
                </span>
                .
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="rejection-note">
              Reason for rejection <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="rejection-note"
              placeholder="Please explain why this time entry is being rejected..."
              value={rejectionNote}
              onChange={(e) => {
                setRejectionNote(e.target.value);
                if (error) {
                  setError("");
                }
              }}
              rows={4}
              className="mt-1.5"
              disabled={isRejecting}
            />
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>
          <p className="text-sm text-slate-500">
            The provider will receive this note and can resubmit corrected
            hours.
          </p>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isRejecting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isRejecting || !rejectionNote.trim()}
            className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
          >
            {isRejecting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rejecting...
              </>
            ) : (
              "Reject Entry"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
